import React, { useState, useEffect } from 'react';
import {
  FiX,
  FiAward,
  FiPercent,
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiUser,
  FiCalendar,
  FiClock,
  FiLayers,
} from 'react-icons/fi';
import { MasterTrader } from '../../types';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { showToast } from '../../utils/toast';

interface CopySettingsModalProps {
  master: MasterTrader;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultFollowerBrokerAccountId?: string | null;
}

const CopySettingsModal: React.FC<CopySettingsModalProps> = ({
  master,
  isOpen,
  onClose,
  onSuccess,
  defaultFollowerBrokerAccountId,
}) => {
  const { user } = useAuthStore();
  const [allocationPercent, setAllocationPercent] = useState<number>(0);
  const [maxInvestmentAmount, setMaxInvestmentAmount] = useState<string>('');
  const [hasTimeLimit, setHasTimeLimit] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [selectedMasterBrokerAccountId, setSelectedMasterBrokerAccountId] = useState<string>('');
  const [selectedFollowerAccountId, setSelectedFollowerAccountId] = useState<string>('');

  // Fetch user's broker connections to get actual account balance
  const { data: connectionsData } = useApi.useBrokerageConnections({
    enabled: isOpen,
  });

  // Fetch per-account allocation data
  const { data: allocationData } = useApi.useAccountAllocation({
    enabled: isOpen,
  });

  // Follower accounts enriched with allocation info
  const followerAccounts = (allocationData?.accounts || []);

  // Auto-select a default follower account when data loads
  useEffect(() => {
    if (!isOpen) return;
    if (followerAccounts.length === 0) return;
    if (selectedFollowerAccountId) return; // already picked
    const fromSidebar = defaultFollowerBrokerAccountId
      ? followerAccounts.find((a) => a.id === defaultFollowerBrokerAccountId)
      : null;
    const primary = fromSidebar || followerAccounts.find((a) => a.isPrimary) || followerAccounts[0];
    if (primary) setSelectedFollowerAccountId(primary.id);
  }, [isOpen, followerAccounts.length, defaultFollowerBrokerAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selected follower account and allocation when modal re-opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedFollowerAccountId('');
      setAllocationPercent(0);
    }
  }, [isOpen]);

  const selectedFollowerAccount = followerAccounts.find((a) => a.id === selectedFollowerAccountId) || null;

  // Calculate balance from the selected follower account
  const calculateTotalBalance = (): number => {
    if (selectedFollowerAccount) return selectedFollowerAccount.accountBalance;
    if (connectionsData?.connections && connectionsData.connections.length > 0) {
      const primary = connectionsData.connections.find(
        (conn: any) => conn.status === 'active' && conn.isPrimary === true
      );
      if (primary) return primary.accountBalance || 0;
      const firstActive = connectionsData.connections.find((conn: any) => conn.status === 'active');
      if (firstActive) return firstActive.accountBalance || 0;
    }
    return user?.accountBalance || 0;
  };

  const totalBalance = calculateTotalBalance();

  // Allocation remaining for the selected follower account (sum of all masters already followed on this account)
  const usedAllocation = selectedFollowerAccount?.usedAllocation ?? 0;
  const remainingAllocation = selectedFollowerAccount?.remainingAllocation ?? 100;
  const wouldExceedAllocation = allocationPercent > remainingAllocation;
  const sliderMax = Math.max(0, remainingAllocation);
  const proposedAllocation = Math.min(allocationPercent, remainingAllocation);

  // Keep this-follow % within what is still free on the selected account
  useEffect(() => {
    if (!isOpen || !selectedFollowerAccountId) return;
    setAllocationPercent((prev) => {
      if (remainingAllocation <= 0) return 0;
      if (prev > remainingAllocation) return remainingAllocation;
      return prev;
    });
  }, [isOpen, selectedFollowerAccountId, remainingAllocation]);

  // Calculate minimum balance requirement based on master's trading type
  const getMinimumBalanceRequirement = (): number => {
    if (master.tradingType === 'swing') {
      return 0; // $20,000 for Swing Trading
    } else if (master.tradingType === 'day') {
      return 0; // $1,000 for Day Trading
    }
    return 0; // No requirement if trading type is not set
  };

  const minimumRequired = getMinimumBalanceRequirement();
  const meetsBalanceRequirement = totalBalance >= minimumRequired;

  // Get broker accounts for this master if available
  const brokerAccounts = (master as any).brokerAccounts || [];
  const hasMultipleBrokerAccounts = brokerAccounts.length > 1;

  // Initialize: select the first master broker account by default
  useEffect(() => {
    if (brokerAccounts.length > 0) {
      setSelectedMasterBrokerAccountId(brokerAccounts[0]._id as string);
    }
  }, [master._id, brokerAccounts.length]);

  const createCheckoutMutation = useApi.useCreateCheckoutSession({
    onError: (error) => {
      showToast('error', error.message || 'Failed to start checkout.');
    },
  });

  const handleEnableCopyError = (err: any) => {
    const errorData = err.response?.data;
    if (errorData?.code === 'MASTER_PAYMENT_REQUIRED' || errorData?.checkoutPlan === 'masterfee' || errorData?.checkoutPlan === 'masterfollowingfee' || errorData?.checkoutPlan === 'pro') {
      const plan = String(user?.plan || '').toLowerCase();
      if (plan !== 'og' && plan !== 'old_user' && plan !== 'if&brice_user') {
        showToast('error', errorData?.error || 'Master follow fee is not required for your plan.');
        return;
      }
      const fee = plan === 'og' ? 99 : plan === 'old_user' ? 299 : null;
      showToast(
        'info',
        fee != null
          ? `Redirecting to checkout to pay $${fee}/mo...`
          : 'Redirecting to checkout to pay master follow fee...'
      );
      const masterId = master?._id;
      createCheckoutMutation.mutate(
        masterId
          ? { plan: 'masterfee', masterId: String(masterId) }
          : 'masterfee'
      );
      onClose();
      return;
    }

    let errorMsg = errorData?.error || 'Failed to enable copy trading';
    if (errorData?.details) {
      errorMsg += `\n\n${errorData.details}`;
    }
    setMessage(errorMsg);
    setMessageType('error');
  };

  const enableCopyMutation = useApi.useEnableCopyTrading({
    onSuccess: (data) => {
      showToast('success', data.message || 'Copy trading enabled successfully!');
      onSuccess();
      onClose();
    },
    onError: handleEnableCopyError,
  });

  const enableBrokerAccountsMutation = useApi.useEnableCopyTradingBrokerAccounts({
    onSuccess: (data) => {
      showToast('success', data.message || 'Copy trading enabled successfully!');
      onSuccess();
      onClose();
    },
    onError: handleEnableCopyError,
  });

  const handleSubmit = () => {
    setMessage('');

    // Must have a follower account selected
    if (!selectedFollowerAccountId && followerAccounts.length > 0) {
      setMessage('Please select which of your brokerage accounts to use');
      setMessageType('error');
      return;
    }

    // Check balance requirement first
    if (minimumRequired > 0 && !meetsBalanceRequirement) {
      const tradingTypeName = master.tradingType === 'swing' ? 'Swing Trading' : 'Day Trading / Hedge Trading';
      setMessage(`Insufficient balance to start copy trading. This master trader is a ${tradingTypeName} specialist. Your current account balance is $${totalBalance.toFixed(2)}. You need at least $${minimumRequired.toLocaleString()} to start copying this master trader.`);
      setMessageType('error');
      return;
    }

    // Allocation cap check
    if (wouldExceedAllocation) {
      const accountLabel = selectedFollowerAccount?.brokerageName || 'this account';
      setMessage(`Your ${accountLabel} account only has ${remainingAllocation}% allocation remaining (${usedAllocation}% already used). Reduce an existing master's allocation or choose a different account.`);
      setMessageType('error');
      return;
    }

    if (allocationPercent < 1 || allocationPercent > 100) {
      setMessage('Please set allocation to at least 1% to follow this master');
      setMessageType('error');
      return;
    }

    if (maxInvestmentAmount && parseFloat(maxInvestmentAmount) <= 0) {
      setMessage('Maximum investment must be greater than 0');
      setMessageType('error');
      return;
    }

    if (hasTimeLimit && !endDate) {
      setMessage('Please select an end date');
      setMessageType('error');
      return;
    }

    if (hasTimeLimit && new Date(endDate) <= new Date()) {
      setMessage('End date must be in the future');
      setMessageType('error');
      return;
    }

    const followerBrokerAccountId = selectedFollowerAccountId || undefined;

    if (brokerAccounts.length > 0 && selectedMasterBrokerAccountId) {
      enableBrokerAccountsMutation.mutate({
        masterUserId: master._id,
        masterBrokerAccountIds: [selectedMasterBrokerAccountId],
        allocationPercent,
        maxInvestmentAmount: maxInvestmentAmount ? parseFloat(maxInvestmentAmount) : undefined,
        endDate: hasTimeLimit ? endDate : undefined,
        followerBrokerAccountId,
      });
    } else {
      enableCopyMutation.mutate({
        masterUserId: master._id,
        allocationPercent,
        maxInvestmentAmount: maxInvestmentAmount ? parseFloat(maxInvestmentAmount) : undefined,
        endDate: hasTimeLimit ? endDate : undefined,
        followerBrokerAccountId,
      });
    }
  };

  if (!isOpen) return null;

  const minEndDate = new Date(Date.now() + 86400000).toISOString().split('T')[0]; // Tomorrow

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FiAward className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Copy Trading Settings</h2>
              <p className="text-sm text-primary-100">Configure how to copy {master.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Master Info Card */}
          <div className="bg-surface rounded-lg p-4 border-2 border-border">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <FiAward className="h-5 w-5 text-primary" />
              Master Trader Details
            </h3>

            {/* Master Broker Account Selection — single select */}
            {brokerAccounts.length > 0 && (
              <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <label className="font-semibold text-foreground flex items-center gap-2 mb-3">
                  <FiCheckCircle className="h-4 w-4 text-primary" />
                  Select Master Broker Account to Follow
                </label>
                <div className="space-y-2">
                  {brokerAccounts.map((account: any) => {
                    const isSelected = selectedMasterBrokerAccountId === account._id;
                    return (
                      <label
                        key={account._id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-surface hover:border-primary/40'
                        }`}
                      >
                        <input
                          type="radio"
                          name="masterBrokerAccount"
                          value={account._id}
                          checked={isSelected}
                          onChange={() => setSelectedMasterBrokerAccountId(account._id)}
                          className="w-4 h-4 accent-primary"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{account.brokerageName}</p>
                          <div className="flex items-center gap-4 text-xs text-muted mt-1">
                            <span>{account.connectionType}</span>
                            <span>{account.accountType === 'paper' ? 'Paper' : 'Live'}</span>
                            {account.isPrimary && (
                              <span className="text-primary font-semibold">Primary</span>
                            )}
                            {account.accountBalance && (
                              <span>${account.accountBalance.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        {isSelected && <FiCheckCircle className="h-5 w-5 text-primary" />}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted mb-1">Name</p>
                <p className="font-semibold text-foreground">{master.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted mb-1">Balance</p>
                <p className="font-semibold text-foreground">
                  ${master.accountBalance?.toLocaleString() || '0'}
                </p>
              </div>
              {master.tradingType && (
                <div>
                  <p className="text-sm text-muted mb-1">Trading Type</p>
                  <p className={`font-semibold text-foreground flex items-center gap-1 ${master.tradingType === 'swing' ? 'text-purple-600' : 'text-blue-600'
                    }`}>
                    {master.tradingType === 'swing' ? (
                      <>
                        <FiCalendar className="h-4 w-4" />
                        Swing Trading
                      </>
                    ) : (
                      <>
                        <FiClock className="h-4 w-4" />
                        Day Trading
                      </>
                    )}
                  </p>
                </div>
              )}
              {master.tradingTypeDescription && (
                <div>
                  <p className="text-sm text-muted mb-1">Requirement</p>
                  <p className="font-semibold text-foreground text-sm">
                    {master.tradingType === 'swing' ? 'Min $20,000' : 'Min $1,000-$2,000'}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted mb-1">Success Rate</p>
                <p className="font-semibold text-success">{master.successRate || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-muted mb-1">Followers</p>
                <p className="font-semibold text-foreground">{master.followerCount || 0}</p>
              </div>
            </div>
          </div>

          {/* Follower Account Selector */}
          {followerAccounts.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FiLayers className="h-5 w-5 text-primary" />
                <label className="block text-sm font-medium text-foreground">
                  Use Which of Your Accounts?
                </label>
              </div>
              <div className="space-y-2">
                {followerAccounts.map((acc) => {
                  const isSelected = selectedFollowerAccountId === acc.id;
                  const isFull = acc.remainingAllocation <= 0;
                  const wouldOverflow = allocationPercent > acc.remainingAllocation;
                  const statusColor = isFull
                    ? 'border-danger/60 bg-danger/5'
                    : wouldOverflow
                    ? 'border-warning/60 bg-warning/5'
                    : isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-surface hover:border-primary/40';

                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => !isFull && setSelectedFollowerAccountId(acc.id)}
                      disabled={isFull}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${statusColor} ${isFull ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-muted'}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div>
                            <span className="font-semibold text-foreground text-sm">
                              {acc.brokerageName}
                            </span>
                            {acc.isPrimary && (
                              <span className="ml-2 text-xs text-primary font-semibold">Primary</span>
                            )}
                            <p className="text-xs text-muted">
                              {acc.connectionType} · ${acc.accountBalance.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {isFull ? (
                            <span className="text-xs font-semibold text-danger">Full (100%)</span>
                          ) : (
                            <>
                              <span className="text-xs font-semibold text-muted">
                                {acc.usedAllocation}% already used
                              </span>
                              <p className={`text-xs font-semibold ${wouldOverflow ? 'text-warning' : 'text-success'}`}>
                                {acc.remainingAllocation}% free for this follow
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Cumulative allocation bar: prior masters → this follow → free */}
                      <div className="mt-2 h-2 bg-surface-elevated rounded-full overflow-hidden">
                        <div className="h-full flex">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${Math.min(acc.usedAllocation, 100)}%` }}
                            title={`${acc.usedAllocation}% already allocated to other masters`}
                          />
                          {isSelected && proposedAllocation > 0 && (
                            <div
                              className={`h-full transition-all ${wouldOverflow ? 'bg-danger/70' : 'bg-info'}`}
                              style={{ width: `${Math.min(proposedAllocation, acc.remainingAllocation)}%` }}
                              title={`+${proposedAllocation}% for this master`}
                            />
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted">
                          <span>
                            <span className="inline-block w-2 h-2 rounded-sm bg-primary mr-1 align-middle" />
                            Other masters {acc.usedAllocation}%
                          </span>
                          <span>
                            <span className="inline-block w-2 h-2 rounded-sm bg-info mr-1 align-middle" />
                            This follow {proposedAllocation}%
                          </span>
                          <span>
                            <span className="inline-block w-2 h-2 rounded-sm bg-surface-elevated border border-border mr-1 align-middle" />
                            Still free {Math.max(0, acc.remainingAllocation - (isSelected ? proposedAllocation : 0))}%
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {wouldExceedAllocation && selectedFollowerAccount && (
                <div className="flex items-start gap-2 text-sm text-warning bg-warning/10 border border-warning/30 rounded-lg p-3">
                  <FiAlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>{selectedFollowerAccount.brokerageName}</strong> only has{' '}
                    <strong>{remainingAllocation}%</strong> remaining. Reduce this allocation,
                    reduce an existing master, or pick a different account.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Allocation Percentage */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FiPercent className="h-5 w-5 text-primary" />
                <label className="block text-sm font-medium text-foreground">
                  Allocation Percentage
                </label>
              </div>
              {usedAllocation > 0 && (
                <span className="text-xs text-muted whitespace-nowrap">
                  {usedAllocation}% already used · {remainingAllocation}% left
                </span>
              )}
            </div>
            <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-4">
              <div className="text-center mb-3">
                <span className="text-4xl font-bold text-primary">{allocationPercent}%</span>
                {usedAllocation > 0 && (
                  <p className="text-xs text-muted mt-1">
                    Account total after this follow:{' '}
                    <span className="font-semibold text-foreground">
                      {Math.min(100, usedAllocation + proposedAllocation)}%
                    </span>
                    {' '}({usedAllocation}% prior + {proposedAllocation}% this master)
                  </p>
                )}
              </div>
              <div className="relative w-full h-5 flex items-center">
                <div className="absolute inset-x-0 h-3 rounded-full bg-muted/50 border border-border overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{
                      width: `${
                        sliderMax > 0
                          ? (Math.min(allocationPercent, sliderMax) / sliderMax) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max={sliderMax || 0}
                  value={Math.min(allocationPercent, sliderMax)}
                  onChange={(e) => setAllocationPercent(Number(e.target.value))}
                  disabled={remainingAllocation <= 0}
                  className="allocation-slider absolute inset-0 w-full h-full z-10"
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted">
                <span>0%</span>
                <span className="font-semibold text-primary">
                  Copy {allocationPercent}% of each trade
                </span>
                <span>{sliderMax}%</span>
              </div>
            </div>

            {/* Balance Requirement Warning */}
            {minimumRequired > 0 && !meetsBalanceRequirement && (
              <div className="bg-danger/10 border-2 border-danger/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiAlertCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-danger mb-2">Balance Requirement Not Met</h4>
                    <p className="text-sm text-foreground mb-2">
                      This master trader is a <strong>{master.tradingType === 'swing' ? 'Swing Trading' : 'Day Trading / Hedge Trading'}</strong> specialist.
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Your Current Balance:</span>
                        <span className="font-semibold text-foreground">${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Minimum Required:</span>
                        <span className="font-semibold text-danger">${minimumRequired.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border">
                        <span className="text-muted">Shortfall:</span>
                        <span className="font-semibold text-danger">
                          ${(minimumRequired - totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted mt-3">
                      You need at least ${minimumRequired.toLocaleString()} in your account to start copying this master trader. This requirement ensures you have sufficient capital to properly copy trades based on the trading strategy.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Allocation Summary */}
            {user && (
              <div className={`bg-gradient-to-br from-primary/10 to-accent/10 border-2 rounded-lg p-4 ${meetsBalanceRequirement ? 'border-primary/30' : 'border-warning/30'
                }`}>
                <div className="flex items-center gap-2 mb-3">
                  <FiDollarSign className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-foreground">Allocation Summary</h4>
                  {meetsBalanceRequirement && minimumRequired > 0 && (
                    <span className="ml-auto px-2 py-1 text-xs bg-success/20 text-success rounded">
                      Requirement Met
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted mb-1">Your Total Balance</p>
                    <p className={`text-2xl font-bold ${meetsBalanceRequirement ? 'text-foreground' : 'text-warning'
                      }`}>
                      ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    {totalBalance === 0 && connectionsData?.connections && connectionsData.connections.length > 0 && (
                      <p className="text-xs text-warning mt-1">
                        Balance may need to be synced
                      </p>
                    )}
                    {totalBalance === 0 && (!connectionsData?.connections || connectionsData.connections.length === 0) && (
                      <p className="text-xs text-muted mt-1">
                        No brokerage connected
                      </p>
                    )}
                    {minimumRequired > 0 && (
                      <p className="text-xs text-muted mt-1">
                        Required: ${minimumRequired.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">This Follow</p>
                    <p className="text-2xl font-bold text-primary">
                      {allocationPercent}%
                    </p>
                    {usedAllocation > 0 && (
                      <p className="text-xs text-muted mt-1">
                        Prior masters: {usedAllocation}%
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 pt-3 border-t border-border">
                    <p className="text-sm text-muted mb-1">Allocated Amount</p>
                    <p className="text-xl font-bold text-success">
                      ${(totalBalance * allocationPercent / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      {allocationPercent}% of ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 text-sm text-foreground bg-info/10 border border-info/30 rounded-lg p-3">
              <FiInfo className="h-4 w-4 text-info flex-shrink-0 mt-0.5" />
              <p>
                Your position sizes will be {allocationPercent}% of the master trader's positions,
                scaled proportionally to your account size. Adjust the slider to see real-time calculations.
              </p>
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${messageType === 'success'
                  ? 'bg-success/10 text-success border-2 border-success/30'
                  : 'bg-danger/10 text-danger border-2 border-danger/30'
                }`}
            >
              {messageType === 'success' ? (
                <FiCheckCircle className="h-5 w-5 flex-shrink-0" />
              ) : (
                <FiAlertCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <span className="font-semibold">{message}</span>
            </div>
          )}

          {/* Summary Box */}
          {/* <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <FiCheckCircle className="h-5 w-5 text-primary" />
              Summary
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-muted">Master Trader:</span>
                <span className="font-semibold text-foreground">{master.name}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-muted">Allocation:</span>
                <span className="font-semibold text-primary">{allocationPercent}%</span>
              </li>
              <li className="flex justify-between">
                <span className="text-muted">Max Investment:</span>
                <span className="font-semibold text-foreground">
                  {maxInvestmentAmount ? `$${parseFloat(maxInvestmentAmount).toLocaleString()}` : 'No limit'}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-muted">Duration:</span>
                <span className="font-semibold text-foreground">
                  {hasTimeLimit && endDate
                    ? new Date(endDate).toLocaleDateString()
                    : 'All Time'}
                </span>
              </li>
            </ul>
          </div> */}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
              disabled={enableCopyMutation.isPending}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className={`flex-1 flex items-center justify-center gap-2 text-lg py-3 rounded-lg font-semibold transition-colors ${
                (minimumRequired > 0 && !meetsBalanceRequirement) || wouldExceedAllocation || allocationPercent < 1
                  ? 'bg-surface-elevated text-foreground border-2 border-border cursor-not-allowed opacity-80'
                  : 'btn-primary text-white'
              }`}
              disabled={
                enableCopyMutation.isPending ||
                enableBrokerAccountsMutation.isPending ||
                (minimumRequired > 0 && !meetsBalanceRequirement) ||
                wouldExceedAllocation ||
                allocationPercent < 1
              }
              title={
                allocationPercent < 1
                  ? 'Set allocation above 0% to follow this master'
                  : wouldExceedAllocation
                  ? `Only ${remainingAllocation}% allocation remaining on this account`
                  : minimumRequired > 0 && !meetsBalanceRequirement
                  ? `You need at least $${minimumRequired.toLocaleString()} to follow this master trader`
                  : ''
              }
            >
              {(enableCopyMutation.isPending || enableBrokerAccountsMutation.isPending) ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Enabling...</span>
                </>
              ) : allocationPercent < 1 ? (
                <>
                  <FiPercent className="h-5 w-5" />
                  <span>Set Allocation to Follow</span>
                </>
              ) : wouldExceedAllocation ? (
                <>
                  <FiAlertCircle className="h-5 w-5" />
                  <span>Allocation Limit Reached</span>
                </>
              ) : (minimumRequired > 0 && !meetsBalanceRequirement) ? (
                <>
                  <FiAlertCircle className="h-5 w-5" />
                  <span>Insufficient Balance</span>
                </>
              ) : (
                <>
                  <FiCheckCircle className="h-5 w-5" />
                  <span>Enable Copy Trading</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopySettingsModal;

